import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  History, Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  Building2, User, Key, Trash2, Edit, Shield, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../utils/api';

// Super Admin API helper
const adminApi = {
  get: (url) => api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
};

const ActivityLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    action: '',
    tenant_id: '',
    search: ''
  });
  const [tenants, setTenants] = useState([]);
  const [actions, setActions] = useState([]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: 25
      });
      if (filters.action) params.append('action', filters.action);
      if (filters.tenant_id) params.append('tenant_id', filters.tenant_id);

      const response = await adminApi.get(`/tenants/activity-logs?${params}`);
      setLogs(response.data.logs);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        totalPages: response.data.total_pages
      });
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [tenantsRes, actionsRes] = await Promise.all([
        adminApi.get('/tenants/'),
        adminApi.get('/tenants/activity-logs/actions')
      ]);
      setTenants(tenantsRes.data);
      setActions(actionsRes.data.actions);
    } catch (error) {
      console.error('Failed to fetch filter options');
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters.action, filters.tenant_id]);

  const getActionIcon = (action) => {
    const iconMap = {
      TENANT_CREATED: Building2,
      TENANT_UPDATED: Edit,
      TENANT_DELETED: Trash2,
      TENANT_STATUS_CHANGED: Shield,
      TENANT_SUBSCRIPTION_CHANGED: CheckCircle,
      USER_CREATED: User,
      USER_DELETED: Trash2,
      USER_STATUS_CHANGED: Shield,
      PASSWORD_RESET: Key,
      ADMIN_PASSWORD_RESET: Key,
      B2B_SETTINGS_CHANGED: Shield,
      LOGIN: User,
    };
    return iconMap[action] || History;
  };

  const getActionBadge = (action) => {
    const config = {
      TENANT_CREATED: { className: 'bg-green-100 text-green-700', label: 'Created' },
      TENANT_UPDATED: { className: 'bg-blue-100 text-blue-700', label: 'Updated' },
      TENANT_DELETED: { className: 'bg-red-100 text-red-700', label: 'Deleted' },
      TENANT_STATUS_CHANGED: { className: 'bg-yellow-100 text-yellow-700', label: 'Status Changed' },
      TENANT_SUBSCRIPTION_CHANGED: { className: 'bg-purple-100 text-purple-700', label: 'Plan Changed' },
      USER_CREATED: { className: 'bg-green-100 text-green-700', label: 'User Created' },
      USER_DELETED: { className: 'bg-red-100 text-red-700', label: 'User Deleted' },
      PASSWORD_RESET: { className: 'bg-orange-100 text-orange-700', label: 'Password Reset' },
      ADMIN_PASSWORD_RESET: { className: 'bg-orange-100 text-orange-700', label: 'Admin Password Reset' },
    }[action] || { className: 'bg-gray-100 text-gray-700', label: action };
    
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const clearFilters = () => {
    setFilters({ action: '', tenant_id: '', search: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-6 h-6 text-purple-600" />
            Activity Log
          </h1>
          <p className="text-gray-500">Track all tenant management actions</p>
        </div>
        <Button variant="outline" onClick={fetchLogs}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label className="text-sm text-gray-500">Action Type</Label>
              <Select 
                value={filters.action || "all"} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, action: v === "all" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actions.map(action => (
                    <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-sm text-gray-500">Tenant</Label>
              <Select 
                value={filters.tenant_id || "all"} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, tenant_id: v === "all" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Tenants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map(tenant => (
                    <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>{tenant.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No activity logs found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Performed By</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Date & Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const ActionIcon = getActionIcon(log.action);
                    return (
                      <TableRow key={log.log_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                              <ActionIcon className="w-4 h-4 text-gray-600" />
                            </div>
                            {getActionBadge(log.action)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.target_name ? (
                            <div>
                              <p className="font-medium">{log.target_name}</p>
                              <p className="text-xs text-gray-400">{log.target_type}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.tenant_name ? (
                            <span className="text-sm">{log.tenant_name}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {log.admin_email || '-'}
                        </TableCell>
                        <TableCell className="text-gray-400 text-xs font-mono">
                          {log.ip_address || '-'}
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {formatDate(log.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Showing {logs.length} of {pagination.total} logs
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLog;
